import { useSyncExternalStore } from "react";
import { qrApi } from "@/api/qr";

/** Foto del QR che salgono appena scelte (Alfonso 18/10, rimedio 2). Prima partivano tutte insieme
 *  all'apertura della chat: dal telefono quasi un minuto d'attesa a chat vuota (9 foto a 6-8 s l'una).
 *
 *  Vive in QrPercorso per tutta la visita (passi Bolletta, Documento, Chat), non nella chat: un rimontaggio
 *  della chat non rimanda nulla. Le foto salgono una alla volta, nell'ordine della pagina, e restano sul
 *  portale («trattieni»): passano al Cat solo con `consegna()`, che la chat chiama DOPO `[batch_inizio n=K]`
 *  — altrimenti il Cat leggerebbe la pagina 1 da sola e presenterebbe le offerte su una bolletta parziale.
 *
 *  La lista è quella che il cliente vede: tolta una pagina già salita, le successive risalgono col numero
 *  nuovo (default approvato il 18/10; il portale tiene l'ultima versione di ogni numero). Un caricamento
 *  fallito ferma la coda: riparte alla prossima azione del cliente (un'altra foto, Avanti) o col «Nuovo
 *  tentativo» della chat, che ne mostra l'errore — nessuna frase nuova nei passi foto. */

type Kind = "bolletta" | "documento";

interface Voce {
  file: File;
  kind: Kind;
  pagina: number | null;          // posto tra le foto della bolletta (da 1); null per PDF e documento
  uploadId: string | null;
  analisiId: string | null;
}

export interface StatoCaricamento {
  totale: number;
  caricate: number;
  errore: boolean;
  analisiIds: string[];
}

type Api = Pick<typeof qrApi, "upload" | "bollettaCompleta" | "consegna">;

export class CaricatoreQr {
  private voci: Voce[] = [];
  private arrivate: Voce[] = [];             // in ordine d'arrivo, per gli id dell'analisi
  private inVolo: Voce | null = null;
  private errore = false;
  private bollettaConfermata = false;
  private bollettaCompletaInviata = false;
  private consegnati = new Set<string>();
  private consegnaInCorso: Promise<void> | null = null;
  private attese: { ok: () => void; ko: (e: Error) => void }[] = [];
  private ascoltatori = new Set<() => void>();
  private istantanea: StatoCaricamento = { totale: 0, caricate: 0, errore: false, analisiIds: [] };
  private readonly token: string;
  private readonly sessionId: string;
  private readonly api: Api;

  constructor(token: string, sessionId: string, api: Api = qrApi) {
    this.token = token;
    this.sessionId = sessionId;
    this.api = api;
  }

  /** La lista che il cliente vede adesso. Le voci già note (stesso file, stesso posto) restano com'erano. */
  imposta(bolletta: File[], documento: File[]): void {
    let foto = 0;
    const volute = [
      ...bolletta.map((file) => ({ file, kind: "bolletta" as Kind, pagina: file.type.startsWith("image/") ? ++foto : null })),
      ...documento.map((file) => ({ file, kind: "documento" as Kind, pagina: null })),
    ];
    const libere = [...this.voci];
    this.voci = volute.map((v) => {
      const i = libere.findIndex((x) => x.file === v.file && x.kind === v.kind && x.pagina === v.pagina);
      return i >= 0 ? libere.splice(i, 1)[0] : { ...v, uploadId: null, analisiId: null };
    });
    this.errore = false;                       // un'azione del cliente rimette in moto una coda ferma
    this.avanza();
    this.notifica();
  }

  /** Il cliente ha finito le foto della bolletta (Avanti): `bolletta-completa` parte quando sono salite tutte. */
  confermaBolletta(): void {
    this.bollettaConfermata = true;
    this.errore = false;
    this.avanza();
    this.notifica();
  }

  riprova(): void {
    this.errore = false;
    this.avanza();
    this.notifica();
  }

  /** K del `[batch_inizio n=K]`: tutte le voci della bolletta, foto e PDF (come prima). */
  numeroBolletta(): number {
    return this.voci.filter((v) => v.kind === "bolletta").length;
  }

  daConsegnare(): boolean {
    return this.voci.some((v) => !v.uploadId || !this.consegnati.has(v.uploadId));
  }

  /** Aspetta che sia salito tutto, poi chiede al portale di passarlo al Cat: bolletta nell'ordine, poi documento.
   *  Due chiamate insieme (rimontaggio della chat) fanno una consegna sola. Rifiuta se un caricamento fallisce. */
  consegna(): Promise<void> {
    if (!this.consegnaInCorso) {
      this.consegnaInCorso = this.tuttoSalito()
        .then(async () => {
          const ids = this.voci.map((v) => v.uploadId as string);
          if (ids.every((id) => this.consegnati.has(id))) return;
          await this.api.consegna(this.token, this.sessionId, ids);
          ids.forEach((id) => this.consegnati.add(id));
        })
        .finally(() => { this.consegnaInCorso = null; });
    }
    return this.consegnaInCorso;
  }

  iscriviti = (fn: () => void): (() => void) => {
    this.ascoltatori.add(fn);
    return () => { this.ascoltatori.delete(fn); };
  };

  stato = (): StatoCaricamento => this.istantanea;

  private tuttoSalito(): Promise<void> {
    if (this.errore) return Promise.reject(new Error("caricamento_fallito"));
    if (!this.inVolo && this.voci.every((v) => v.uploadId)) return Promise.resolve();
    return new Promise((ok, ko) => { this.attese.push({ ok, ko }); });
  }

  private avanza(): void {
    if (this.inVolo || this.errore) return;
    const prossima = this.voci.find((v) => !v.uploadId);
    if (!prossima) {
      this.forseBollettaCompleta();
      this.attese.splice(0).forEach((a) => a.ok());
      return;
    }
    this.inVolo = prossima;
    this.api.upload(this.token, this.sessionId, prossima.file, prossima.kind, {
      ...(prossima.pagina !== null ? { pagina: prossima.pagina } : {}),
      trattieni: true,
    })
      .then((r) => {
        if (!r.uploadId) throw new Error("upload_senza_id");
        prossima.uploadId = r.uploadId;
        prossima.analisiId = r.analisiId;
        this.arrivate.push(prossima);
      })
      .catch(() => {
        this.errore = true;
        this.attese.splice(0).forEach((a) => a.ko(new Error("caricamento_fallito")));
      })
      .finally(() => {
        this.inVolo = null;
        this.avanza();
        this.notifica();
      });
  }

  private forseBollettaCompleta(): void {
    if (!this.bollettaConfermata || this.bollettaCompletaInviata) return;
    const pagine = this.voci.filter((v) => v.kind === "bolletta" && v.pagina !== null).length;
    if (pagine === 0) return;                  // bolletta di soli PDF: non si chiama (default approvato 18/10)
    this.bollettaCompletaInviata = true;
    // se non riesce, l'analisi legge comunque quello che ha dopo l'attesa massima: non ferma il cliente
    this.api.bollettaCompleta(this.token, this.sessionId, pagine).catch(() => {});
  }

  private notifica(): void {
    // id dell'analisi: quelli delle foto restano anche se la pagina è stata tolta (il capofila legge il gruppo
    // intero), quelli dei PDF solo se il PDF è ancora nella lista
    const analisiIds = this.arrivate
      .filter((v) => v.analisiId && (v.pagina !== null || this.voci.includes(v)))
      .map((v) => v.analisiId as string);
    const nuova: StatoCaricamento = {
      totale: this.voci.length,
      caricate: this.voci.filter((v) => v.uploadId).length,
      errore: this.errore,
      analisiIds: analisiIds.join() === this.istantanea.analisiIds.join() ? this.istantanea.analisiIds : analisiIds,
    };
    const prima = this.istantanea;
    if (nuova.totale === prima.totale && nuova.caricate === prima.caricate && nuova.errore === prima.errore
        && nuova.analisiIds === prima.analisiIds) return;
    this.istantanea = nuova;
    this.ascoltatori.forEach((fn) => fn());
  }
}

export function useCaricamento(c: CaricatoreQr): StatoCaricamento {
  return useSyncExternalStore(c.iscriviti, c.stato, c.stato);
}
